/**
 * กระบี่จันทร์เสน่หา (listening-blade): its lock routine and its own ท่าใหญ่ (kit audit docs/design/combat/2026-10-13-art-kit-audit.md
 * piece 7).
 *
 * Lock (design docs/design/combat/2026-10-11-lock-moves-13.md §2.4): the lock move is ms-kiss 吻 (range 2.2, lifesteal 0.45
 * on every routine blow; the 0.35 s lock wind-up: free hand out palm-up as if inviting a partner, blade low behind the back),
 * not the rules-sweep placeholder ms-moon. Identity: a partner dance at touching distance, slow then sudden; the finisher
 * leaves the foe charmed (`endStatus`, at the let-go). Contrast: chorus works at sleeve length and accelerates.
 *
 * Ult ms-eclipse (design docs/design/combat/2026-10-11-long-moves.md §1.3): its own table, off the dash template it shared
 * with ss-hatred (no passes, no lift, no vortex, no dive). The sky dims, six cuts out of the dark (blinks: the caster lands
 * as far behind the foe as it stood), a moon disc bites across, one pale crescent. Grounded and quiet where snow is airborne.
 * Opener numbers kept from the template entry (field r 3.4, tell 0.8 s, len 4.4, power 3.7) for ns-balance.
 *
 * Keys (ms* poses, moon/crescent/drain fx, silk/blade/chime sfx) are the contract for the motion / FX / sound lanes. Data only.
 */
import { registerLongMove } from './registry';

/** 吻 ระบำใต้จันทร์เสี้ยว — takes the hand, two turns under the arm, the dip, three touches that drain, the spin-away cut. */
export const CRESCENT_DANCE = registerLongMove({
  skillId: 'ms-kiss',
  kind: 'lock',
  name: 'ระบำใต้จันทร์เสี้ยว',
  opener: { type: 'contact', range: 0, tell: 0 },
  len: 5.6,
  catch: 0.5,
  power: 2.2,
  lunge: 0.8,
  endStatus: { status: 'charm', dur: 4 },
  beats: [
    { at: 0, pose: 'msTakeHand', hits: 0, share: 0, foe: 'hit', fx: 'crescentShadow', sfx: 'silkRustle', cam: 'hold' },
    { at: 0.5, pose: 'msTurnUnder', hits: 1, share: 0.1, foe: 'spin', ai: 2, fx: 'crescentTrail', sfx: 'bladeWhisper', cam: 'orbit' },
    { at: 1.4, pose: 'msTurnFast', hits: 1, share: 0.1, foe: 'spin', ai: 2, fx: 'crescentTrail', sfx: 'bladeWhisper2', cam: 'orbit' },
    { at: 2.2, pose: 'msDip', hits: 0, share: 0, foe: 'kneel', fx: 'moonMotes', sfx: 'heartLow', cam: 'push' },
    { at: 3.0, pose: 'msTipTouch', hits: 3, share: 0.3, foe: 'kneel', fx: 'drainThread', sfx: 'drainHum' },
    {
      at: 4.0,
      pose: 'msSpinAway',
      hits: 1,
      share: 0.5,
      foe: 'knock',
      dist: 1.2,
      stop: 0.08,
      fx: 'crescentCut',
      sfx: 'moonChime',
      cam: 'shake',
    },
    { at: 4.6, pose: 'msBow', hits: 0, share: 0, foe: 'fall' },
    { at: 5.1, pose: 'msStance', hits: 0, share: 0, foe: 'recover' },
  ],
});

/** 蝕 จันทราคราส — the sky dims, six cuts out of the dark, the moon disc bites across, the last crescent. */
export const ECLIPSE = registerLongMove({
  skillId: 'ms-eclipse',
  kind: 'ult',
  name: 'จันทราคราส',
  opener: {
    type: 'field',
    range: 0,
    radius: 3.4,
    tell: 0.8,
    tellPose: 'msEclipseRaise',
    tellFx: 'eclipseRing',
    tellSfx: 'msRageFlare',
  },
  len: 4.4,
  power: 3.7,
  lunge: 0,
  whiff: { pose: 'msSpent', fx: 'moonScatter', sfx: 'msSpent', stop: 0.9, rageBack: 10 },
  beats: [
    { at: 0, pose: 'msDim', hits: 1, share: 0.04, foe: 'hit', fx: 'eclipseDim', sfx: 'eclipseHush', cam: 'pull', stop: 0.05 },
    {
      at: 0.35,
      pose: 'msBlinkA',
      hits: 2,
      share: 0.09,
      foe: 'spin',
      move: { kind: 'blink' },
      ai: 2,
      fx: 'moonShadowCut',
      sfx: 'blinkCut1',
      stop: 0.04,
    },
    {
      at: 0.8,
      pose: 'msBlinkB',
      hits: 2,
      share: 0.09,
      foe: 'spin',
      move: { kind: 'blink' },
      ai: 2,
      fx: 'moonShadowCut',
      sfx: 'blinkCut2',
      stop: 0.04,
    },
    {
      at: 1.25,
      pose: 'msBlinkC',
      hits: 2,
      share: 0.1,
      foe: 'kneel',
      move: { kind: 'blink' },
      ai: 3,
      fx: 'moonShadowCut',
      sfx: 'blinkCut3',
      cam: 'orbit',
      stop: 0.04,
    },
    { at: 1.8, pose: 'msDiscRaise', hits: 0, share: 0, foe: 'kneel', fx: 'eclipseDisc', sfx: 'discHum', cam: 'push' },
    { at: 2.3, pose: 'msDiscBite', hits: 3, share: 0.18, foe: 'hit', fx: 'discBite', sfx: 'discBite3', stop: 0.03 },
    {
      at: 2.9,
      pose: 'msCrescent',
      hits: 1,
      share: 0.5,
      foe: 'knock',
      dist: 1.4,
      ai: 3,
      fx: 'eclipseCrescent',
      sfx: 'eclipseChime',
      cam: 'shake',
      stop: 0.16,
    },
    { at: 3.4, pose: 'msSheathe', hits: 0, share: 0, foe: 'fall', fx: 'moonReturn', sfx: 'moonReturn' },
    { at: 3.9, pose: 'msStance', hits: 0, share: 0, foe: 'recover', fx: 'eclipseFade', sfx: 'msExhale' },
  ],
});
